"use client";

import React, { useEffect, useMemo, useState } from "react";
import type {
  AdminColegioAsignacion,
  AdminColegioCurso,
  AdminColegioProfesor,
  ReporteAsistenciaCurso,
  ReporteProgresoAlumno,
} from "../../../app/admin-colegio/administrar/dto/admin.dto";

import { adminColegioAdministracionService } from "../../../services/admin-colegio/administracion.service";
import AdminColegioLayout from "../components/admincolegiolayout";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import {
  Users,
  BookOpen,
  ClipboardList,
  CheckCircle2,
  XCircle,
  TrendingUp,
  BarChart3,
} from "lucide-react";

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="rounded-2xl border bg-white px-4 py-3 shadow-lg">
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      <p className="text-sm text-gray-600">
        Valor:{" "}
        <span className="font-bold text-purple-700">{payload[0].value}</span>
      </p>
    </div>
  );
};

const DonutCenterText = ({
  total,
  label,
}: {
  total: number;
  label: string;
}) => {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <p className="text-3xl font-extrabold text-gray-900">{total}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

const formatDateDDMMYYYY = (yyyyMMdd: string) => {
  const [y, m, d] = yyyyMMdd.split("-");
  return `${d}/${m}/${y}`;
};

const AdminColegioDashboardPage: React.FC = () => {
  const [asignaciones, setAsignaciones] = useState<AdminColegioAsignacion[]>(
    []
  );
  const [cursos, setCursos] = useState<AdminColegioCurso[]>([]);
  const [profesores, setProfesores] = useState<AdminColegioProfesor[]>([]);
  const [loading, setLoading] = useState(true);

  const [reporteAsistencia, setReporteAsistencia] =
    useState<ReporteAsistenciaCurso | null>(null);
  const [reporteProgreso, setReporteProgreso] =
    useState<ReporteProgresoAlumno | null>(null);

  const [errorReportes, setErrorReportes] = useState<string | null>(null);

  const periodo = useMemo(() => {
    const hoy = new Date();
    const year = hoy.getFullYear();
    const month = hoy.getMonth() + 1; // 1-12

    const inicio = month <= 6 ? `${year}-01-01` : `${year}-07-01`;

    const fin = `${year}-${String(month).padStart(2, "0")}-${String(
      hoy.getDate()
    ).padStart(2, "0")}`;

    return { inicio, fin };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [cursosData, profesoresData, asignacionesData] =
          await Promise.allSettled([
            adminColegioAdministracionService.getCursos(),
            adminColegioAdministracionService.getProfesores(),
            adminColegioAdministracionService.getAsignaciones().catch(() => []),
          ]);

        if (cursosData.status === "fulfilled") setCursos(cursosData.value);
        else setCursos([]);

        if (profesoresData.status === "fulfilled")
          setProfesores(profesoresData.value);
        else setProfesores([]);

        if (asignacionesData.status === "fulfilled")
          setAsignaciones(asignacionesData.value);
        else setAsignaciones([]);

        try {
          setErrorReportes(null);

          const [asistenciaRes, progresoRes] = await Promise.allSettled([
            adminColegioAdministracionService.getReporteAsistencia({
              periodoInicio: periodo.inicio,
              periodoFin: periodo.fin,
            }),
            adminColegioAdministracionService.getReporteProgreso({
              periodoInicio: periodo.inicio,
              periodoFin: periodo.fin,
            }),
          ]);

          if (asistenciaRes.status === "fulfilled") {
            setReporteAsistencia(asistenciaRes.value);
          } else {
            setReporteAsistencia(null);
          }

          if (progresoRes.status === "fulfilled") {
            setReporteProgreso(progresoRes.value);
          } else {
            setReporteProgreso(null);
          }

          if (
            asistenciaRes.status === "rejected" &&
            progresoRes.status === "rejected"
          ) {
            setErrorReportes("No se pudieron cargar los reportes.");
          }
        } catch {
          setErrorReportes("No se pudieron cargar los reportes.");
          setReporteAsistencia(null);
          setReporteProgreso(null);
        }
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [periodo.inicio, periodo.fin]);

  const totalAsignaciones = asignaciones.length;

  const totalVigentes = useMemo(
    () => asignaciones.filter((a) => a.vigente).length,
    [asignaciones]
  );

  const totalNoVigentes = useMemo(
    () => asignaciones.filter((a) => !a.vigente).length,
    [asignaciones]
  );

  const porcentajeVigentes = useMemo(() => {
    if (totalAsignaciones === 0) return 0;
    return Math.round((totalVigentes / totalAsignaciones) * 100);
  }, [totalVigentes, totalAsignaciones]);

  const pieData = useMemo(
    () => [
      { name: "Vigentes", value: totalVigentes },
      { name: "No vigentes", value: totalNoVigentes },
    ],
    [totalVigentes, totalNoVigentes]
  );

  const barData = useMemo(
    () => [
      { name: "Profesores", total: profesores.length },
      { name: "Cursos", total: cursos.length },
      { name: "Asignaciones", total: asignaciones.length },
    ],
    [profesores.length, cursos.length, asignaciones.length]
  );

  const asistenciaPorCursoData = useMemo(() => {
    if (!reporteAsistencia?.cursos?.length) return [];

    return reporteAsistencia.cursos.map((curso) => {
      let asistencias = 0;
      let inasistencias = 0;

      curso.profesores?.forEach((p) => {
        p.alumnos?.forEach((a) => {
          asistencias += Number(a.asistencias ?? 0);
          inasistencias += Number(a.inasistencias ?? 0);
        });
      });

      const total = asistencias + inasistencias;
      const porcentaje =
        total === 0 ? 0 : Math.round((asistencias / total) * 100);

      return {
        curso: curso.nombre,
        asistencias,
        inasistencias,
        porcentajeAsistencia: porcentaje,
      };
    });
  }, [reporteAsistencia]);

  const progresoPorCursoData = useMemo(() => {
    if (!reporteProgreso?.cursos?.length) return [];

    return reporteProgreso.cursos.map((curso) => {
      let sumaEnsayos = 0;
      let sumaActividades = 0;
      let sumaAsistencia = 0;
      let count = 0;

      curso.profesores?.forEach((p) => {
        p.alumnos?.forEach((a) => {
          const ensayos = Number(a.indicadores?.promedioEnsayos ?? 0);
          const actividades = Number(a.indicadores?.promedioActividades ?? 0);
          const asistencia = Number(a.indicadores?.asistenciaPorcentaje ?? 0);

          if (Number.isFinite(ensayos)) sumaEnsayos += ensayos;
          if (Number.isFinite(actividades)) sumaActividades += actividades;
          if (Number.isFinite(asistencia)) sumaAsistencia += asistencia;

          count += 1;
        });
      });

      const promedioEnsayos =
        count === 0 ? 0 : Number((sumaEnsayos / count).toFixed(2));
      const promedioActividades =
        count === 0 ? 0 : Number((sumaActividades / count).toFixed(2));
      const asistenciaPromedio =
        count === 0 ? 0 : Number((sumaAsistencia / count).toFixed(0));

      return {
        curso: curso.nombre,
        promedioEnsayos,
        promedioActividades,
        asistenciaPromedio,
      };
    });
  }, [reporteProgreso]);

  const topAsistencia = useMemo(() => {
    if (asistenciaPorCursoData.length === 0) return null;
    return [...asistenciaPorCursoData].sort(
      (a, b) => b.porcentajeAsistencia - a.porcentajeAsistencia
    )[0];
  }, [asistenciaPorCursoData]);

  const topEnsayos = useMemo(() => {
    if (progresoPorCursoData.length === 0) return null;
    return [...progresoPorCursoData].sort(
      (a, b) => b.promedioEnsayos - a.promedioEnsayos
    )[0];
  }, [progresoPorCursoData]);

  const COLORS = ["#22c55e", "#ef4444"];

  if (loading)
    return (
      <AdminColegioLayout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="w-14 h-14 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-purple-700 font-semibold">
            Cargando datos...
          </p>
        </div>
      </AdminColegioLayout>
    );

  return (
    <AdminColegioLayout>
      <div className="min-h-screen bg-gray-50/60 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-6">
        <div className="relative overflow-hidden rounded-3xl p-8 text-white mb-10 bg-gradient-to-r from-purple-600 via-purple-700 to-purple-950 shadow-lg">
          <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="absolute -bottom-10 -left-10 w-52 h-52 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold mb-2">
                Panel Administrador Colegio
              </h1>
              <p className="text-purple-100">
                Estadísticas generales + reportes (histórico completo)
              </p>
              <p className="text-purple-200 text-sm mt-2">
                Semestre actual:{" "}
                <span className="font-semibold">
                  {formatDateDDMMYYYY(periodo.inicio)}
                </span>{" "}
                →{" "}
                <span className="font-semibold">
                  {formatDateDDMMYYYY(periodo.fin)}
                </span>
              </p>
            </div>

            <a
              href="/admin-colegio/administrar"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 border border-white/20 rounded-xl transition shadow"
            >
              <span className="font-semibold">Ir a administrar</span>
            </a>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 relative z-10">
            <span className="bg-white/15 border border-white/20 px-3 py-1 rounded-full text-sm">
              {porcentajeVigentes}% asignaciones vigentes
            </span>

            {topAsistencia ? (
              <span className="bg-white/15 border border-white/20 px-3 py-1 rounded-full text-sm">
                Mejor asistencia: {topAsistencia.curso} (
                {topAsistencia.porcentajeAsistencia}%)
              </span>
            ) : null}

            {topEnsayos ? (
              <span className="bg-white/15 border border-white/20 px-3 py-1 rounded-full text-sm">
                Mejor ensayo: {topEnsayos.curso} ({topEnsayos.promedioEnsayos})
              </span>
            ) : null}
          </div>
        </div>

        {errorReportes ? (
          <div className="mb-8 bg-white border rounded-2xl p-4 text-sm text-gray-700 shadow-sm">
            <span className="font-semibold text-red-600">Aviso:</span>{" "}
            {errorReportes}
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
          <div className="bg-white rounded-3xl p-6 shadow-sm border hover:shadow-md transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                <Users className="text-purple-700" />
              </div>
              <span className="text-xs text-gray-400">Total</span>
            </div>
            <p className="text-gray-500 text-sm">Profesores</p>
            <p className="text-4xl font-extrabold text-gray-900 mt-1">
              {profesores.length}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Registrados en el colegio
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border hover:shadow-md transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                <BookOpen className="text-purple-700" />
              </div>
              <span className="text-xs text-gray-400">Total</span>
            </div>
            <p className="text-gray-500 text-sm">Cursos</p>
            <p className="text-4xl font-extrabold text-gray-900 mt-1">
              {cursos.length}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Disponibles para asignación
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border hover:shadow-md transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                <ClipboardList className="text-purple-700" />
              </div>
              <span className="text-xs text-gray-400">Total</span>
            </div>
            <p className="text-gray-500 text-sm">Asignaciones</p>
            <p className="text-4xl font-extrabold text-gray-900 mt-1">
              {asignaciones.length}
            </p>
            <p className="text-xs text-gray-400 mt-2">Creadas en el sistema</p>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm border hover:shadow-md transition">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="text-green-600" />
              </div>
              <span className="text-xs text-gray-400">Estado</span>
            </div>
            <p className="text-gray-500 text-sm">Vigencia</p>

            <div className="mt-2 flex items-center gap-2">
              <span className="text-lg font-bold text-green-700">
                {totalVigentes} vigentes
              </span>
            </div>

            <div className="mt-1 flex items-center gap-2">
              <XCircle size={18} className="text-red-500" />
              <span className="text-sm text-red-600">
                {totalNoVigentes} no vigentes
              </span>
            </div>

            <div className="mt-3">
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full"
                  style={{
                    width:
                      totalAsignaciones === 0
                        ? "0%"
                        : `${(totalVigentes / totalAsignaciones) * 100}%`,
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">Progreso de vigencia</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-10">
          <div className="bg-white p-6 rounded-3xl shadow-sm border min-w-0">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Totales generales
                </h2>
                <p className="text-sm text-gray-500">
                  Comparación rápida entre módulos
                </p>
              </div>
              <span className="text-xs bg-purple-50 text-purple-700 px-3 py-1 rounded-full border border-purple-100 flex items-center gap-2">
                <BarChart3 size={14} />
                Resumen
              </span>
            </div>

            <div className="w-full min-h-[300px] h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData} barSize={52}>
                  <defs>
                    <linearGradient
                      id="barGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="4 4" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="total"
                    name="Total"
                    fill="url(#barGradient)"
                    radius={[16, 16, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-3xl shadow-sm border min-w-0">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Estado de asignaciones
                </h2>
                <p className="text-sm text-gray-500">
                  Distribución vigentes vs no vigentes
                </p>
              </div>
              <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-100">
                {porcentajeVigentes}% vigentes
              </span>
            </div>

            <div className="relative w-full min-h-[320px] h-[320px]">
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={40} />
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={75}
                    outerRadius={115}
                    paddingAngle={3}
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <DonutCenterText
                total={totalAsignaciones}
                label="Total asignaciones"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-10">
          <div className="bg-white p-6 rounded-3xl shadow-sm border min-w-0">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Asistencia por curso
                </h2>
                <p className="text-sm text-gray-500">
                  Asistencias vs Inasistencias
                </p>
              </div>

              <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-100 flex items-center gap-2">
                <TrendingUp size={14} />
                Asistencia
              </span>
            </div>

            {!reporteAsistencia ? (
              <div className="p-6 rounded-2xl border bg-gray-50 text-gray-600 text-sm">
                No hay reporte de asistencia disponible para el periodo.
              </div>
            ) : (
              <div className="w-full min-h-[320px] h-[320px]">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={asistenciaPorCursoData} barSize={28}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} />
                    <XAxis
                      dataKey="curso"
                      tick={{ fill: "#6b7280", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#6b7280", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, "dataMax + 5"]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0)
                          return null;

                        const row = payload[0]?.payload;

                        return (
                          <div className="rounded-2xl border bg-white px-4 py-3 shadow-lg">
                            <p className="text-sm font-semibold text-gray-900">
                              {label}
                            </p>
                            <p className="text-sm text-gray-600">
                              Asistencias:{" "}
                              <span className="font-bold text-blue-600">
                                {row?.asistencias ?? 0}
                              </span>
                            </p>
                            <p className="text-sm text-gray-600">
                              Inasistencias:{" "}
                              <span className="font-bold text-red-600">
                                {row?.inasistencias ?? 0}
                              </span>
                            </p>
                            <p className="text-sm text-gray-600">
                              % Asistencia:{" "}
                              <span className="font-bold text-purple-700">
                                {row?.porcentajeAsistencia ?? 0}%
                              </span>
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Legend />

                    <Bar
                      dataKey="asistencias"
                      name="Asistencias"
                      fill="#3b82f6"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="inasistencias"
                      name="Inasistencias"
                      fill="#ef4444"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-3xl shadow-sm border min-w-0">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Progreso por curso
                </h2>
                <p className="text-sm text-gray-500">Ensayos + Actividades</p>
              </div>

              <span className="text-xs bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-100 flex items-center gap-2">
                <BarChart3 size={14} />
                Progreso
              </span>
            </div>

            {!reporteProgreso ? (
              <div className="p-6 rounded-2xl border bg-gray-50 text-gray-600 text-sm">
                No hay reporte de progreso disponible para el periodo.
              </div>
            ) : (
              <div className="w-full min-h-[320px] h-[320px]">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={progresoPorCursoData} barSize={18}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} />
                    <XAxis
                      dataKey="curso"
                      tick={{ fill: "#6b7280", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#6b7280", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      domain={[
                        0,
                        (dataMax: number) => (dataMax < 1 ? 1 : dataMax + 1),
                      ]}
                    />

                    <Tooltip content={<CustomTooltip />} />
                    <Legend />

                    <Bar
                      dataKey="promedioEnsayos"
                      name="Prom. Ensayos"
                      fill="#f59e0b"
                      radius={[10, 10, 0, 0]}
                    />

                    <Bar
                      dataKey="promedioActividades"
                      name="Prom. Actividades"
                      fill="#8b5cf6"
                      radius={[10, 10, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Profesores del Colegio
              </h2>
              <p className="text-sm text-gray-500">
                Listado rápido de docentes registrados
              </p>
            </div>

            <a
              href="/admin-colegio/administrar"
              className="px-4 py-2 bg-purple-600 text-white rounded-xl shadow hover:bg-purple-700 transition"
            >
              Administrar
            </a>
          </div>

          <div className="overflow-x-auto rounded-2xl border">
            <table className="w-full table-auto">
              <thead className="bg-purple-600 text-white sticky top-0">
                <tr>
                  <th className="p-4 text-left font-semibold">Nombre</th>
                  <th className="p-4 text-left font-semibold">Email</th>
                  <th className="p-4 text-left font-semibold">Activo</th>
                </tr>
              </thead>
              <tbody>
                {profesores.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-8 text-center text-gray-500">
                      No hay profesores disponibles
                    </td>
                  </tr>
                ) : (
                  profesores.map((profesor) => (
                    <tr
                      key={profesor.idUsuario}
                      className="border-t hover:bg-gray-50 transition"
                    >
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {profesor.nombres} {profesor.apellidos}
                          </span>
                          <span className="text-xs text-gray-400">
                            ID: {profesor.idUsuario}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-gray-700">{profesor.email}</td>
                      <td className="p-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${profesor.vigente
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                            }`}
                        >
                          {profesor.vigente ? "Sí" : "No"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminColegioLayout>
  );
};

export default AdminColegioDashboardPage;
